import {
  estimatePaymentTermItemFromJSON,
  EstimatePaymentTermItemRecord,
  IEstimatePaymentTermItemData,
} from "common/records/PaymentTermItem";
import { List, Record } from "immutable";
import { round } from "lodash";

export const estimatePaymentTermsFromJSON = (json: Partial<IEstimatePaymentTermsData>): EstimatePaymentTermsRecord => {
  const record: IEstimatePaymentTermsRecord = { ...(json as any) };

  if (json.payment_term_items) {
    record.payment_term_items = List(json.payment_term_items.map((pti) => estimatePaymentTermItemFromJSON(pti)));
  }

  return new EstimatePaymentTermsRecord(record);
};

/**
 * Result of calculating a single payment term item - immutable
 */
interface PaymentTermItemResult {
  readonly calculated: number;
  readonly remainingValue: number;
}

/**
 * Pure function to calculate a single payment term item's effect
 * No mutations - returns new values
 */
export const performPaymentTermItem = (
  item: EstimatePaymentTermItemRecord,
  baseValue: number,
  remainingValue: number,
): PaymentTermItemResult => {
  const calculated = (() => {
    switch (item.type) {
      case "percent":
        return round(baseValue * (item.amount / 100), 2);
      case "amount":
        return item.amount;
      default:
        return 0;
    }
  })();

  return {
    calculated,
    remainingValue: remainingValue - calculated,
  };
};

/**
 * Immutable accumulator for payment term item reduction
 */
interface PaymentTermItemAccumulator {
  readonly remainingValue: number;
  readonly updatedItems: List<EstimatePaymentTermItemRecord>;
}

export const isPaymentTermsFinanced = (payment_terms: EstimatePaymentTermsRecord): boolean =>
  payment_terms.payment_term_items.some((item) => item.paymentMethod === "financed");

/**
 * Pure function to calculate all payment term items
 * Uses reduce with immutable accumulator - no mutations
 *
 * Algorithm:
 * 1. First process "amount" type items (flat amounts deducted from total)
 * 2. Then process "percent" type items (percentage of remaining after amounts)
 * 3. Reconcile any rounding discrepancy in the last item
 * 4. Sum `financed_amount` from items flagged `financed` ($0 when none), then derive `down_payment`
 *    as `total − financed_amount`, gated to $0 unless a financed item is present
 */
export const calcPaymentTerms = (
  payment_terms: EstimatePaymentTermsRecord,
  total: number,
): {
  readonly payment_terms: EstimatePaymentTermsRecord;
} => {
  // Group items by type for ordered processing
  const amountItems = payment_terms.payment_term_items.filter((item) => item.type === "amount");
  const percentItems = payment_terms.payment_term_items.filter((item) => item.type === "percent");

  // Process "amount" items first (they use the full total as base)
  const afterAmounts = amountItems.reduce(
    (acc: PaymentTermItemAccumulator, item: EstimatePaymentTermItemRecord): PaymentTermItemAccumulator => {
      const result = performPaymentTermItem(item, total, acc.remainingValue);
      return {
        remainingValue: result.remainingValue,
        updatedItems: acc.updatedItems.push(item.set("calculated", result.calculated)),
      };
    },
    { remainingValue: total, updatedItems: List<EstimatePaymentTermItemRecord>() },
  );

  // Process "percent" items using the remaining value after amounts as the base
  const afterPercents = percentItems.reduce(
    (acc: PaymentTermItemAccumulator, item: EstimatePaymentTermItemRecord): PaymentTermItemAccumulator => {
      const result = performPaymentTermItem(item, afterAmounts.remainingValue, acc.remainingValue);
      return {
        remainingValue: result.remainingValue,
        updatedItems: acc.updatedItems.push(item.set("calculated", result.calculated)),
      };
    },
    { remainingValue: afterAmounts.remainingValue, updatedItems: afterAmounts.updatedItems },
  );

  // Reconcile rounding discrepancy in the last item
  const allItems = afterPercents.updatedItems;
  const sum = allItems.reduce((memo: number, item: EstimatePaymentTermItemRecord) => memo + item.calculated, 0);
  const diff = total - sum;

  const reconciledItems = (() => {
    if (allItems.size === 0) {
      return allItems;
    }
    const lastItem = allItems.last();
    if (!lastItem) {
      return allItems;
    }
    return allItems.set(allItems.size - 1, lastItem.set("calculated", lastItem.calculated + diff));
  })();

  const hasFinancedItem = isPaymentTermsFinanced(payment_terms);
  const financedAmount = reconciledItems.reduce((memo: number, item: EstimatePaymentTermItemRecord) => {
    return item.paymentMethod === "financed" ? memo + item.calculated : memo;
  }, 0);
  const downPayment = hasFinancedItem ? total - financedAmount : 0;

  // Preserve original order while applying updates
  const orderedUpdatedItems = payment_terms.payment_term_items.map((originalItem) => {
    const updated = reconciledItems.find((item) => item.uuid === originalItem.uuid);
    return updated ?? originalItem;
  });

  return {
    payment_terms: payment_terms.merge({
      payment_term_items: orderedUpdatedItems,
      financed_amount: financedAmount,
      down_payment: downPayment,
    }),
  };
};

export interface IEstimatePaymentTermsData {
  id: number;
  name: string;
  description: string;
  financed_amount: number;
  down_payment: number;
  payment_term_items: IEstimatePaymentTermItemData[];
}

export interface IEstimatePaymentTermsRecord {
  id: number;
  name: string;
  description: string;
  financed_amount: number;
  down_payment: number;
  payment_term_items: List<EstimatePaymentTermItemRecord>;
}

const defaultEstimatePaymentTermProps: IEstimatePaymentTermsRecord = {
  id: 0,
  name: "",
  description: "",
  financed_amount: 0,
  down_payment: 0,
  payment_term_items: List<EstimatePaymentTermItemRecord>(),
};

export class EstimatePaymentTermsRecord
  extends Record(defaultEstimatePaymentTermProps)
  implements IEstimatePaymentTermsRecord {}
